/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useState} from "react";
import ArgosFlexbox from "../../Containers/ArgosFlexbox";
import ArgosButton from "../../Buttons/ArgosButton";
import {IArgosDialogProps} from "../../Dialogs/ArgosDialog";

import {usePostRequest} from "../../../../api/helper";
import toast from "react-hot-toast";
import ADAS_PROGRAMS_CONSTANTS from "../constants";
import Loader from "../../../components/Loader/Loader";
import styled from "styled-components";
import BoxWithCenteredContent from "../../Containers/BoxWithCenteredContent";
import {useAppDispatch, useAppSelector} from "../../../../redux/hooks";
import {selectAdasProgramsMetaData} from "../../../../redux/ducks/AdasPrograms/slice";
import {manageDataSourcesDialogSlice} from "../../../pages/AdasPrograms/ItemDetails/common/Dialogs/ManageDataSourcesDialog";
import {AdasProgramsReportPages} from "../../../pages/AdasPrograms/ItemDetails/common/constants";
import {DialogTree} from "./SelectSourceDatasetsDialog/DialogTree/DialogTree";

export enum EJamaTreeNodeType {
  PROJECT = 'projects',
  REPORT = 'reports',
  ROOT = 'root',
  ITEMS = 'items',
}

const minMaxWidth = '40vw'
export const ADAS_DIALOG_STYLE: Partial<IArgosDialogProps> = {
  // maxWidth={'fit-content'}
  minWidth: minMaxWidth,
  maxWidth: minMaxWidth,
  height: '60vh',
  headerTitleAlign: 'left',
}

const SELECT_SOURCE_MIN_MAX_WIDTH = '50vw'
export const SELECT_DATASET_DIALOG_STYLE: Partial<IArgosDialogProps> = {
  headerTitleAlign: 'left',
  height: '90vh',
  minWidth: SELECT_SOURCE_MIN_MAX_WIDTH,
  maxWidth: SELECT_SOURCE_MIN_MAX_WIDTH,
  headerTitle: `Edit Node`,
}

export const LEFT_PANEL_ITEMS_PADDING = 5


interface FilterDatasetsDialogProps {
  page: AdasProgramsReportPages
}

export const FilterDatasetsDialog = (props: FilterDatasetsDialogProps) => {
  const dispatch = useAppDispatch()

  const {page} = props

  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const adasProgramId = adasProgramMetaData?.adasProgramId

  const [selectedDatasets, setSelectedDatasets] = useState<any>(null)
  const [datasetsByStudy, setDatasetsByStudy] = useState<any>(null)
  const [disabledNodes, setDisabledNodes] = useState<any>(null)
  const isPlanOfRecord = useAppSelector((state) => selectAdasProgramsMetaData(state)?.isPlanOfRecord)
  const saveFilteredDatasetsHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.getSaveOrFetchDatasetsEndpoint('save'))
  const [isLoadingSaveFilteredDatasets, setIsLoadingSaveFilteredDatasets] = useState(false)

  const handleSave = () => {
    const _newObject: any = {}
    const datasetIdsSet: any = {}
    Object.keys(selectedDatasets).forEach((key) => {
      const nodeVal = selectedDatasets[key]
      // If this has property redlinesList, then add every redline to the new object.
      if (nodeVal?.redlinesList) {
        const redlinesList = nodeVal.redlinesList
        Object.keys(redlinesList).forEach((_key) => {
          const _redlineNode = redlinesList[_key]
          const _redlineId = _redlineNode?.datasetId
          _newObject[_redlineId] = _redlineNode
          datasetIdsSet[_redlineId] = true
        })
      }
      _newObject[key] = nodeVal
      datasetIdsSet[key] = true
    })
    setIsLoadingSaveFilteredDatasets(true)
    saveFilteredDatasetsHandle({adasProgramId, page, filteredDatasets: datasetIdsSet}).then((_res) => {
    }).catch((err) => {
      console.log(err)
      toast.error('Error saving selected datasets')
    }).finally(() => {
      dispatch(manageDataSourcesDialogSlice[page].setIsDialogOpen(false))  /** Close dialog */
      setIsLoadingSaveFilteredDatasets(false)
    })
  }

  const fetchFilteredDatasetsHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.getSaveOrFetchDatasetsEndpoint('fetch'))
  const [isLoadingFetchFilteredDatasets, setIsLoadingFetchFilteredDatasets] = useState(false)
  const fetchFilteredDatasets = () => {
    if (!adasProgramId) return
    setIsLoadingFetchFilteredDatasets(true)
    fetchFilteredDatasetsHandle({adasProgramId, page}).then((_fetchedFilteredDatasets) => {
      setSelectedDatasets(_fetchedFilteredDatasets)
    }).catch((err) => {
      console.log(err)
      toast.error('Error fetching selected datasets')
    }).finally(() => {
      setIsLoadingFetchFilteredDatasets(false)
    })
  }


  const fetchRedlineDatasetsHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchDatasetsEndpoint)
  const [isLoadingFetchRedlineDatasets, setIsLoadingFetchRedlineDatasets] = useState(false)
  const [errorFetchRedlineDatasets, setErrorFetchRedlineDatasets] = useState(false)
  const fetchRedlineDatasets = () => {
    if (!adasProgramId) return
    setIsLoadingFetchRedlineDatasets(true)
    fetchRedlineDatasetsHandle({adasProgramId, page}).then((_fetchedRedlineDatasetsByStudy) => {
      const _tree = _fetchedRedlineDatasetsByStudy?.tree
      setDatasetsByStudy(_tree)
      const disabledNodes = _fetchedRedlineDatasetsByStudy?.disabledNodes
      setDisabledNodes(disabledNodes)
      setErrorFetchRedlineDatasets(false)
    }).catch((err) => {
      console.log(err)
      toast.error('Error fetching selected datasets')
      setErrorFetchRedlineDatasets(true)
    }).finally(() => {
      setIsLoadingFetchRedlineDatasets(false)
    })
  }

  useEffect(() => {
    fetchRedlineDatasets()
    fetchFilteredDatasets()
  }, [adasProgramId])

  const isSaveDisabled = isLoadingFetchFilteredDatasets || isLoadingFetchRedlineDatasets || isLoadingSaveFilteredDatasets
    || !selectedDatasets

  const emptyText = 'Merged data sets have not been created for this adas program.'
  const errorText = 'Error fetching merged data sets for this adas program.'
  const planOfRecordInfoText: string = "\n\nThis ADAS Program is Plan of Record. Merged data sets that are plan of record have not been created for this program."

  return (
    <ArgosFlexbox vertical style={{height: '100%'}}>
      <Loader
        isLoading={isLoadingFetchRedlineDatasets || isLoadingFetchFilteredDatasets || isLoadingSaveFilteredDatasets}>
        {(errorFetchRedlineDatasets || !datasetsByStudy) ?
          <BoxWithCenteredContent vertical horizontal style={{}}>
            {!isPlanOfRecord && (errorFetchRedlineDatasets ? errorText : emptyText)}
            {isPlanOfRecord && planOfRecordInfoText}
          </BoxWithCenteredContent>
          :
          <DialogTree
            sourceData={datasetsByStudy}
            selectedDatasets={selectedDatasets}
            setSelectedDataset={(_selectedDatasets: any) => {
              setSelectedDatasets(_selectedDatasets)
            }}
            isMultiSelect={true}
            fromSelectSource={true}
            disabledNodes={disabledNodes}
          />}
      </Loader>
      <ArgosFlexbox style={{justifySelf: 'flex-end', justifyContent: 'center', marginTop: 'auto'}}>
        <ArgosButton
          onClick={() => dispatch(manageDataSourcesDialogSlice[page].setIsDialogOpen(false))}
        >
          Cancel
        </ArgosButton>
        <ArgosButton
          onClick={() => handleSave()}
          disabled={isSaveDisabled}
        >
          Save
        </ArgosButton>
      </ArgosFlexbox>
    </ArgosFlexbox>
  )
}


export const ArgosContextMenuListItem = styled.li({
  // '&:hover': {
  //   cursor: 'pointer',
  //   backgroundColor: theme.layout.sidebar.footer.collapseButton.hoverColor
  // },
  // padding: 5
})

